import { api } from './client';

export interface SyncDevice {
  id: string;
  name: string;
  type: 'desktop' | 'tablet' | 'mobile';
  platform?: string;
  lastSync: Date;
  syncStatus: 'synced' | 'syncing' | 'pending' | 'error';
  tabCount: number;
  isOnline?: boolean;
}

export interface SyncLog {
  id: string;
  action: string;
  device: string;
  timestamp: Date;
  tabsAffected: number;
}

export interface SyncStatus {
  status: 'synced' | 'syncing' | 'pending' | 'error';
  lastSync: string;
  pendingItems: number;
}

export interface SyncHistoryItem {
  id: string;
  action: string;
  device: string;
  timestamp: string;
  tabsAffected: number;
}

function mapDeviceType(type?: string, platform?: string): SyncDevice['type'] {
  const value = (type ?? platform ?? '').toLowerCase();
  if (value.includes('phone') || value === 'ios' || value === 'android' || value === 'mobile') return 'mobile';
  if (value.includes('tablet') || value === 'ipad') return 'tablet';
  return 'desktop';
}

function mapApiDevice(d: {
  id: string;
  name: string;
  platform?: string;
  type?: string;
  is_online?: boolean;
  last_seen?: string;
  last_active?: string;
  tab_count?: number;
}): SyncDevice {
  const lastSeen = d.last_seen ?? d.last_active ?? new Date().toISOString();
  return {
    id: d.id,
    name: d.name,
    type: mapDeviceType(d.type, d.platform),
    platform: d.platform,
    lastSync: new Date(lastSeen),
    syncStatus: d.is_online ? 'synced' : 'pending',
    tabCount: d.tab_count ?? 0,
    isOnline: d.is_online,
  };
}

export async function fetchSyncStatus(): Promise<SyncStatus> {
  const { data } = await api.get<{ status: string; last_sync: string; pending: number }>('/sync/status');
  return {
    status: data.status as SyncStatus['status'],
    lastSync: data.last_sync,
    pendingItems: data.pending,
  };
}

export async function triggerSync(): Promise<{ triggered: boolean }> {
  const { data } = await api.post<{ triggered: boolean }>('/sync/trigger');
  return data;
}

export async function fetchSyncHistory(): Promise<SyncHistoryItem[]> {
  const { data } = await api.get<{ history: SyncHistoryItem[] }>('/sync/history');
  return data.history ?? [];
}

export async function fetchDevices(): Promise<SyncDevice[]> {
  const { data } = await api.get<{
    devices: Array<{
      id: string;
      name: string;
      platform?: string;
      type?: string;
      is_online?: boolean;
      last_seen?: string;
      tab_count?: number;
    }>;
  }>('/cross-platform/devices');
  return (data.devices ?? []).map(mapApiDevice);
}

export async function removeDevice(id: string): Promise<void> {
  await api.delete(`/cross-platform/devices/${encodeURIComponent(id)}`);
}

export async function fetchCrossPlatformDevices(): Promise<SyncDevice[]> {
  return fetchDevices();
}

export async function triggerCrossPlatformSync(): Promise<{ status: string }> {
  const { data } = await api.post<{ status: string }>('/cross-platform/sync');
  return data;
}
